import React, { useMemo, useCallback } from 'react';
import posthog from 'posthog-js';
import ProductListView from './ProductListView';
import { filterProducts, getCollectionProducts } from '../../../../utils/collections';
import { useStore } from '../../../../store';
import { isUserLoggedIn } from '../../../../utils/analytics';

const MAX_PRODUCTS = 16;

type ProductListContainerProps = {
  products: Shopify.ProductEdge[] | null;
  sectionTitle: string;
  linkText?: string;
  href: string;
  titleForTracking: string;
  filterType?: 'liveCampaigns' | 'petitions' | 'endingSoon' | 'comingSoon' | null;
};

const ProductListContainer: React.FC<ProductListContainerProps> = ({
  products,
  sectionTitle,
  linkText = 'View All',
  href,
  titleForTracking,
  filterType = null,
}) => {
  const { state } = useStore();

  const processedProducts = useMemo(() => {
    if (!products) return null;

    let filtered = products;

    switch (filterType) {
      case 'liveCampaigns':
        filtered = filterProducts(products, false, false, true);
        break;
      case 'petitions':
        filtered = getCollectionProducts(products, false, false);
        break;
      case 'endingSoon':
        filtered = getCollectionProducts(products, true);
        break;
      case 'comingSoon':
        filtered = getCollectionProducts(products);
        break;
      default:
        filtered = products;
    }

    return filtered.slice(0, MAX_PRODUCTS);
  }, [products, filterType]);

  const handleViewAllClick = useCallback(() => {
    posthog.capture('homepage_productList_viewAll_click', {
      section_title: sectionTitle,
      link_href: href,
      link_title: linkText,
      is_logged_in: isUserLoggedIn(state.user),
    });
  }, [sectionTitle, href, linkText, state.user]);

  if (!processedProducts || processedProducts.length === 0) {
    return null;
  }

  return (
    <ProductListView
      products={processedProducts}
      sectionTitle={sectionTitle}
      linkText={linkText}
      href={href}
      titleForTracking={titleForTracking}
      onClick={handleViewAllClick}
    />
  );
};

export default ProductListContainer;
